import { ENDPOINT } from '~/constants';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { HUBSPOT_IMPORT_DEFAULT_NAME_COLUMN } from './constants';
import {
  type HubspotImportMatchResponse,
  type HubspotImportMatchVariables,
} from './types';
import {
  zodHubspotImportMatchEndpointSchema,
  zodHubspotImportMatchRequestSchema,
  zodHubspotImportMatchResponseSchema,
} from './validations';

/**
 * Uploads a HubSpot deal export for a buyer company and returns the supplier
 * companies it was matched against, each with the score it was matched at.
 *
 * `validate.body` still runs `zodHubspotImportMatchRequestSchema` for logging
 * drift, but a `FormData` body bypasses zod's `.default()`/`.transform()`
 * entirely (see the FormData branch of `processRequestBody` in
 * `packages/data/src/kyClient.ts`), so every field the backend should receive
 * — the schema's own default included — has to be appended here explicitly.
 *
 * @param companyId - The UUID of the buyer company the suppliers belong to.
 * @param requestParams - The XLSX or CSV export and the column to read names from.
 * @returns The matches as a flat list, ungrouped. Confidence is the caller's
 * to derive from each match's score — see `HIGH_CONFIDENCE_SCORE_THRESHOLD`.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin---Companies/operation/post_hubspot_import_vadmin_company__company_id__hubspot_import_post
 */
export const hubspotImportMatch = async ({
  companyId,
  requestParams,
}: HubspotImportMatchVariables) => {
  const formData = new FormData();
  formData.append('file', requestParams.file);
  // `||`, not `??`: a caller that leaves the column field blank means "use the
  // default" just as much as one that omits it, and the backend cannot read an
  // empty column name.
  formData.append(
    'nameColumn',
    requestParams.nameColumn?.trim() || HUBSPOT_IMPORT_DEFAULT_NAME_COLUMN,
  );
  if (requestParams.customerNumberColumn?.trim()) {
    formData.append('customerNumberColumn', requestParams.customerNumberColumn);
  }

  try {
    const response = await vestigasApi
      .post(ENDPOINT.COMPANY.HUBSPOT_IMPORT.MATCH(companyId), {
        body: formData,
        validate: {
          body: zodHubspotImportMatchRequestSchema,
          pathSegments: zodHubspotImportMatchEndpointSchema,
          response: zodHubspotImportMatchResponseSchema,
        },
      })
      .json<HubspotImportMatchResponse>();

    return response.data;
  } catch (error) {
    log.error({
      message: 'Error matching HubSpot supplier import',
      error,
    });

    // No toast here: the import modal renders this error inline next to the file
    // input, because the user has to act on it by retrying or picking another file.
    throw error;
  }
};
